import { ListPage, Badge, AsyncBoundary, KpiCard } from '@kollez/design-system';
import { usePartnerColleges, type PartnerCollegeRow } from '@/lib/queries';

/**
 * Company partner colleges (F63). Every college the recruiter has an MoU with, plus per-college
 * offer counts (released + accepted) drawn from Application + Offer aggregates.
 *
 * MoU expiry badge tinted red < 30d, orange < 60d — same rule as group/mous, mirrored for recruiter side.
 */
export function CompanyCollegesPage() {
  const { data, isLoading, error, refetch } = usePartnerColleges();
  const rows = data?.colleges ?? [];

  const totals = {
    total: rows.length,
    active: rows.filter((r) => r.mouActive).length,
    expiring: rows.filter((r) => r.daysToMouExpiry != null && r.daysToMouExpiry >= 0 && r.daysToMouExpiry <= 60).length,
    accepted: rows.reduce((s, r) => s + r.offersAccepted, 0),
  };

  return (
    <div className="stack">
      {/* Header lives outside AsyncBoundary so it renders during loading + empty states too. */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Partners</p>
          <h1 className="h-page">Partner colleges</h1>
          <p className="subhead">Every college you have a signed MoU with. Hire history is per college, this season and cumulative.</p>
        </div>
      </div>

      <AsyncBoundary
        kind="table"
        loading={isLoading}
        error={error}
        empty={!isLoading && rows.length === 0}
        emptyTitle="No partner colleges yet"
        emptyHint="Once a college signs an MoU with your company, it lands here."
        onRetry={() => void refetch()}
      >

        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Colleges" value={totals.total} />
          <KpiCard label="Active MoUs" value={totals.active} tone="success" />
          <KpiCard label="Expiring in 60d" value={totals.expiring} tone={totals.expiring ? 'warning' : 'success'} />
          <KpiCard label="Total accepted" value={totals.accepted} tone="info" />
        </div>

        <ListPage<PartnerCollegeRow>
          eyebrow="All colleges"
          title="By college"
          filters={[
            { key: 'type', label: 'Type', options: ['Engineering', 'Management', 'Pharmacy', 'Degree'] },
          ]}
          segment={{
            key: 'mouActive' as never,
            options: [['all', 'All'], ['true', 'MoU active']],
          }}
          rows={rows}
          rowKey={(r) => r._id}
          columns={[
            {
              key: 'name',
              label: 'College',
              render: (r) => (
                <div>
                  <div className="cell-title">{r.name}</div>
                  <div className="cell-sub">{r.code} · {r.type}</div>
                </div>
              ),
            },
            {
              key: 'students',
              label: 'Students',
              right: true,
              hideMobile: true,
              render: (r) => r.totalStudents ? r.totalStudents.toLocaleString('en-IN') : '—',
            },
            {
              key: 'accepted',
              label: 'Accepted',
              right: true,
              render: (r) => (
                <Badge tone={r.offersAccepted > 0 ? 'success' : 'outline'}>{r.offersAccepted}</Badge>
              ),
            },
            {
              key: 'released',
              label: 'Released',
              right: true,
              hideMobile: true,
              render: (r) => r.offersReleased,
            },
            {
              key: 'mou',
              label: 'MoU',
              right: true,
              render: (r) =>
                !r.mouActive ? (
                  <Badge tone="warning">inactive</Badge>
                ) : r.daysToMouExpiry != null && r.daysToMouExpiry <= 60 ? (
                  <Badge tone={r.daysToMouExpiry <= 30 ? 'danger' : 'warning'}>
                    {r.daysToMouExpiry}d left
                  </Badge>
                ) : (
                  <Badge tone="success">active</Badge>
                ),
            },
          ]}
          drawer={(r) => ({
            eyebrow: r.type,
            title: r.name,
            body: (
              <div className="grid gap-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-ink-tertiary">College code</span><div><b>{r.code}</b></div></div>
                  <div><span className="text-ink-tertiary">Total students</span><div><b>{r.totalStudents ?? '—'}</b></div></div>
                  <div><span className="text-ink-tertiary">Offers released</span><div><b>{r.offersReleased}</b></div></div>
                  <div><span className="text-ink-tertiary">Offers accepted</span><div><b>{r.offersAccepted}</b></div></div>
                </div>
                {!r.mouActive && (
                  <div className="p-3 rounded bg-bg-warning/10 text-xs">
                    <b>MoU inactive.</b> Ask the college's TPO to sign a fresh MoU before posting drives.
                  </div>
                )}
              </div>
            ),
          })}
        />
      </AsyncBoundary>
    </div>
  );
}
